import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { FileText, Upload, CheckCircle, Warning, Clock } from '@phosphor-icons/react';
import { toast } from 'sonner';

const apiBase = () => {
  const env = process.env.REACT_APP_BACKEND_URL;
  if (env && env.includes('.preview.emergentagent.com') &&
      !window.location.hostname.includes('.preview.emergentagent.com')) {
    return window.location.origin;
  }
  return env || window.location.origin;
};

export default function PublicUploadPage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploaderName, setUploaderName] = useState('');
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${apiBase()}/api/public/upload/${token}`);
      setInfo(res.data);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Link is invalid or expired';
      setError({ status: err.response?.status, detail });
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const onFile = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    let ok = 0; let fail = 0;
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append('file', file);
        if (uploaderName.trim()) fd.append('uploader_name', uploaderName.trim());
        await axios.post(`${apiBase()}/api/public/upload/${token}`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        ok += 1;
      } catch (err) {
        fail += 1;
        toast.error(`${file.name}: ${err.response?.data?.detail || 'Upload failed'}`);
      }
    }
    setUploading(false);
    e.target.value = '';
    if (ok) toast.success(`${ok} file${ok > 1 ? 's' : ''} uploaded`);
    await load();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary/30">
        <div className="text-muted-foreground text-sm">Loading…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary/30 p-6">
        <div className="max-w-md w-full bg-white border border-border rounded p-6 text-center">
          <Warning size={48} className="mx-auto text-red-500 mb-3" weight="duotone" />
          <h1 className="text-xl font-bold mb-2">Link not available</h1>
          <p className="text-muted-foreground text-sm">{error.detail}</p>
          <p className="text-xs text-muted-foreground mt-4">
            If you believe this is a mistake, please contact the 360 Carexport team and ask for a fresh link.
          </p>
        </div>
      </div>
    );
  }

  const remaining = (info.max_uploads || 0) - (info.uploads_count || 0);
  const limitReached = remaining <= 0;
  const expiresAt = info.expires_at ? new Date(info.expires_at) : null;

  return (
    <div className="min-h-screen bg-secondary/30 py-8 px-4" data-testid="public-upload-page">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white border border-border rounded p-6 mb-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-primary text-white flex items-center justify-center font-bold text-sm rounded">
              360
            </div>
            <div>
              <h1 className="font-bold text-lg leading-tight">360 Carexport</h1>
              <p className="text-xs text-muted-foreground">Upload your documents</p>
            </div>
          </div>
          <div className="border-t border-border pt-3 mt-3 grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="overline text-[10px] uppercase text-muted-foreground">Order #</p>
              <p className="font-mono font-bold">{info.order_number}</p>
            </div>
            <div>
              <p className="overline text-[10px] uppercase text-muted-foreground">Client</p>
              <p className="font-medium">{info.client_name || '—'}</p>
            </div>
          </div>
          {info.cars?.length > 0 && (
            <div className="border-t border-border pt-3 mt-3 space-y-1">
              <p className="overline text-[10px] uppercase text-muted-foreground">Vehicles</p>
              {info.cars.map((c, i) => (
                <p key={i} className="text-sm">
                  {c.brand} {c.model}{c.color ? ` · ${c.color}` : ''}
                  {c.vin ? <span className="font-mono text-xs text-muted-foreground ml-2">{c.vin}</span> : null}
                  {c.end_client_name ? <span className="text-muted-foreground ml-1"> — {c.end_client_name}</span> : null}
                </p>
              ))}
            </div>
          )}
          {info.note_to_client && (
            <div className="border-t border-border pt-3 mt-3">
              <p className="overline text-[10px] uppercase text-muted-foreground">Note from our team</p>
              <p className="text-sm whitespace-pre-line">{info.note_to_client}</p>
            </div>
          )}
          {expiresAt && (
            <div className="flex items-center gap-2 mt-3 text-xs text-muted-foreground">
              <Clock size={12} /> Link expires on {expiresAt.toLocaleDateString()} at {expiresAt.toLocaleTimeString()}
            </div>
          )}
        </div>

        <div className="bg-white border border-border rounded p-6 mb-4">
          <h2 className="font-bold mb-3 flex items-center gap-2">
            <Upload size={18} /> Upload documents
          </h2>
          <p className="text-xs text-muted-foreground mb-4">
            Allowed: images (PNG / JPG / WEBP / HEIC) and PDF files, up to 15 MB each.
            You have <span className="font-bold">{remaining}</span> upload{remaining === 1 ? '' : 's'} remaining on this link.
          </p>
          <div className="mb-3">
            <Label className="text-xs">Your name (optional)</Label>
            <Input
              value={uploaderName}
              onChange={(e) => setUploaderName(e.target.value)}
              placeholder="So our team knows who sent it"
              data-testid="uploader-name-input"
            />
          </div>
          {limitReached ? (
            <div className="border-2 border-dashed border-amber-300 bg-amber-50 p-4 text-center text-sm">
              You've reached the upload limit on this link. Please contact our team if you need to send more documents.
            </div>
          ) : (
            <label className="block border-2 border-dashed border-primary/40 bg-primary/5 hover:bg-primary/10 cursor-pointer p-6 text-center transition-colors">
              <input
                type="file"
                multiple
                accept="image/*,application/pdf"
                onChange={onFile}
                disabled={uploading}
                className="hidden"
                data-testid="public-upload-input"
              />
              <Upload size={32} className="mx-auto text-primary mb-2" weight="duotone" />
              <p className="text-sm font-medium">
                {uploading ? 'Uploading…' : 'Click to choose files or drop them here'}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                You can select multiple files at once.
              </p>
            </label>
          )}
        </div>

        {info.existing_documents?.length > 0 && (
          <div className="bg-white border border-border rounded p-6">
            <h2 className="font-bold mb-3 flex items-center gap-2">
              <CheckCircle size={18} className="text-green-600" weight="duotone" />
              Already uploaded ({info.existing_documents.length})
            </h2>
            <div className="space-y-2">
              {info.existing_documents.map((d) => (
                <div key={d.id} className="flex items-center justify-between border border-border rounded p-2 text-sm">
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText size={20} className="shrink-0 text-muted-foreground" />
                    <div className="min-w-0">
                      <p className="truncate font-medium">{d.original_filename}</p>
                      <p className="text-[11px] text-muted-foreground">
                        {((d.size || 0) / 1024).toFixed(1)} KB · {new Date(d.created_at).toLocaleString()}
                        {d.uploaded_source === 'client_link' ? ' · by you' : ' · by our team'}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <p className="text-center text-[11px] text-muted-foreground mt-6">
          Secure upload by 360 Carexport. Your files are only visible to our team and you.
        </p>
      </div>
    </div>
  );
}
